// Opening the microphone, picking a container the browser will actually produce, and reading the
// speech back while it happens. What is left for each module is what it does with the result.
export class VoiceCapture {
  static MIME_CANDIDATES = [ "audio/webm;codecs=opus", "audio/webm", "audio/mp4" ]
  static FFT_SIZE = 1024
  // A microphone that never answers would leave this waiting forever.
  static OPEN_TIMEOUT = 12_000

  #stream = null
  #recorder = null
  #audioContext = null
  #analyser = null
  #sampleBuffer = null
  #chunks = []
  #mimeType = "audio/webm"
  #starting = false

  constructor({ onData = () => {}, onStop = () => {} } = {}) {
    this.onData = onData
    this.onStop = onStop
  }

  get available() {
    return Boolean(window.isSecureContext && navigator.mediaDevices?.getUserMedia)
  }

  get recording() {
    return Boolean(this.#recorder && this.#recorder.state !== "inactive")
  }

  get busy() {
    return this.#starting || Boolean(this.#stream)
  }

  get mimeType() {
    return this.#mimeType
  }

  // Answers with the error name the browser gave, or null once the microphone is open: what to say
  // about it is not this object's call.
  async open() {
    this.#starting = true
    try {
      this.#stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({ audio: true }),
        new Promise((_, refuse) =>
          setTimeout(() => refuse(new Error("TimeoutError")), VoiceCapture.OPEN_TIMEOUT))
      ])
      return null
    } catch (error) {
      return error?.name || error?.message || "UnknownError"
    } finally {
      this.#starting = false
    }
  }

  start() {
    this.#setupAnalyser()

    const type = VoiceCapture.MIME_CANDIDATES.find((candidate) =>
      window.MediaRecorder?.isTypeSupported?.(candidate))

    this.#recorder = type ?
      new MediaRecorder(this.#stream, { mimeType: type }) : new MediaRecorder(this.#stream)
    this.#mimeType = this.#recorder.mimeType || type || "audio/webm"
    this.#recorder.ondataavailable = (event) => {
      if (event.data.size) this.#chunks.push(event.data)
      this.onData(event)
    }
    this.#recorder.onstop = () => this.onStop()
    this.#recorder.start()
  }

  stop() {
    if (this.recording) this.#recorder.stop()
  }

  get paused() {
    return this.#recorder?.state === "paused"
  }

  // Asking for the data before pausing is what makes a partial preview possible: the chunks so far
  // are only handed over when the recorder is told to hand them over.
  pauseAfterFlush() {
    if (!this.recording) return

    try { this.#recorder.requestData() } catch {  }
    this.#recorder.pause()
  }

  resume() {
    if (this.paused) this.#recorder.resume()
  }

  // The loudest sample since the last read, on the 0..1 scale the waveform draws in.
  level() {
    if (!this.#analyser) return 0

    this.#analyser.getByteTimeDomainData(this.#sampleBuffer)
    let sum = 0
    for (const sample of this.#sampleBuffer) {
      const normalized = (sample - 128) / 128
      sum += normalized * normalized
    }
    return Math.min(1, Math.sqrt(sum / this.#sampleBuffer.length) * 2.2)
  }

  recorded() {
    return new Blob(this.#chunks, { type: this.#mimeType })
  }

  release() {
    // ending the tracks can stop the recorder by itself, and by then whoever asked for this is
    // already gone: the answer would arrive at nobody
    if (this.#recorder) this.#recorder.onstop = null
    this.#stream?.getTracks()?.forEach((track) => track.stop())
    this.#stream = null
    this.#recorder = null
    this.#audioContext?.close()
    this.#audioContext = null
    this.#analyser = null
    this.#chunks = []
  }

  #setupAnalyser() {
    this.#audioContext = new (window.AudioContext || window.webkitAudioContext)()
    this.#analyser = this.#audioContext.createAnalyser()
    this.#analyser.fftSize = VoiceCapture.FFT_SIZE
    this.#sampleBuffer = new Uint8Array(this.#analyser.fftSize)
    this.#audioContext.createMediaStreamSource(this.#stream).connect(this.#analyser)
  }
}
